<script>
    import { onMount, onDestroy, tick, createEventDispatcher } from "svelte";
    import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
    import toast from "svelte-french-toast";
    import { fade } from "svelte/transition";

    const dispatch = createEventDispatcher();

    export let isInline = false;

    let html5Qrcode;
    let isScanning = false;
    let hasScanned = false;
    let availableCameras = [];
    let selectedCameraId = "";
    let isPermissionDenied = false; // 💡 카메라 권한 차단 상태 플래그

    // 💡 스캔 결과 로그 (풀스크린/인라인 공통, 최근 30건만 유지)
    let scanLogs = [];
    let isScanLogExpanded = false;
    const SCAN_LOG_MAX = 30;

    function pushScanLog(type, message) {
        scanLogs = [
            { type, message, time: new Date().toLocaleTimeString("ko-KR", { hour12: false }) },
            ...scanLogs,
        ].slice(0, SCAN_LOG_MAX);
    }

    // 💡 부모 컴포넌트가 스캔 판정(성공/실패) 결과를 알려주기 위해 호출하는 함수.
    // 풀스크린 모드에서는 실패 시 화면을 닫지 않고 로그만 남긴 뒤 스캔을 재개한다.
    export function reportResult(success, message) {
        if (message) {
            pushScanLog(success ? "success" : "error", message);
        }

        if (success) {
            if (!isInline) {
                stopScanning();
            }
        } else {
            // 💡 같은 바코드를 카메라가 계속 비추고 있는 동안 재판정이 연속으로 쏟아지는 것을 막기 위해
            // 인라인 모드와 동일하게 최소 쿨다운을 준 뒤 스캔 락을 해제한다.
            setTimeout(() => {
                hasScanned = false;
            }, 1500);
        }
    }

    let isPopStateTriggered = false;
    let isHistoryPushed = false;

    function handlePopState(e) {
        isPopStateTriggered = true;
        stopScanning();
    }

    onMount(async () => {
        // 뒤로가기 버튼 입력 시 화면만 종료하도록 가짜 히스토리 추가 (인라인 모달 팝업 형태일 때만 적용)
        if (!isInline) {
            try {
                window.history.pushState({ scannerOpen: true }, "");
                isHistoryPushed = true;
                window.addEventListener("popstate", handlePopState);
            } catch (e) {
                console.warn("스캐너 뒤로가기 방지 상태 등록 실패:", e);
            }
        }

        // 카메라 기기 목록 조회
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length > 0) {
                availableCameras = devices;
                // 후면 카메라 우선 선택 (label에 back, environment, 후면 포함)
                const backCam = devices.find(
                    (d) =>
                        d.label.toLowerCase().includes("back") ||
                        d.label.toLowerCase().includes("environment") ||
                        d.label.toLowerCase().includes("후면"),
                );
                selectedCameraId = backCam ? backCam.id : devices[0].id;
            }
            // 즉시 스캔 시작
            await startScanning();
        } catch (err) {
            console.warn("카메라 목록 조회 실패:", err);
            isPermissionDenied = true; // 권한 오류 상태 활성화
            toast.error("카메라 장치를 찾을 수 없거나 권한이 없습니다.");
        }
    });

    async function startScanning() {
        if (isScanning) return;
        isScanning = true;

        await tick();

        if (!html5Qrcode) {
            html5Qrcode = new Html5Qrcode("reader", {
                // 제조 현장 LOT 번호 스캔에 필요한 핵심 포맷 3가지로 압축 (불필요한 포맷 대조 연산 제거)
                formatsToSupport: [
                    Html5QrcodeSupportedFormats.CODE_128,
                    Html5QrcodeSupportedFormats.CODE_39,
                    Html5QrcodeSupportedFormats.QR_CODE,
                ],
                useBarCodeDetectorIfSupported: true,
                experimentalFeatures: {
                    useBarCodeDetectorIfSupported: true
                }
            });
        }

        try {
            // 카메라 스펙 구성 (선택된 카메라 ID 혹은 후면 카메라)
            const videoConstraints = {};
            if (selectedCameraId) {
                videoConstraints.deviceId = { exact: selectedCameraId };
            } else {
                videoConstraints.facingMode = "environment";
            }
            // 성능 및 인식 속도 최적화를 위해 바코드 인식에 가장 적합한 720p 해상도 지정
            videoConstraints.width = { ideal: 1280 };
            videoConstraints.height = { ideal: 720 };

            const config = {
                fps: 15, // 초당 디코딩 시도 횟수를 15회로 조정 (과부하를 방지하여 카메라 화면 지연 및 블러 현상을 막고 최종 인식 속도를 극대화)
                aspectRatio: 1.777778, // 16:9 비율 유지
                disableFlip: true, // 불필요한 좌우 반전 이미지 중복 디코딩(2중 연산) 방지
                videoConstraints: videoConstraints,
                qrbox: (width, height) => {
                    // 실제 비디오 영역 크기(viewfinder)를 기준으로 조준 상자 크기(480x220)에 맞게
                    // 바코드 인식 영역(Region of Interest)을 제한합니다.
                    const boxWidth = Math.min(480, width * 0.9);
                    const boxHeight = Math.min(220, height * 0.9);
                    return {
                        width: Math.floor(boxWidth),
                        height: Math.floor(boxHeight),
                    };
                },
            };

            const cameraSpec = selectedCameraId
                ? selectedCameraId
                : { facingMode: "environment" };

            await html5Qrcode.start(
                cameraSpec,
                config,
                (decodedText, decodedResult) => {
                    onScanSuccess(decodedText, decodedResult);
                },
                (errorMessage) => {
                    // 무시
                },
            );
        } catch (err) {
            console.error("스캐너 시작 오류:", err);
            html5Qrcode = null;
            isScanning = false;
            
            const errMsg = err.message || "";
            // 브라우저/웹뷰 단 권한 거부 오류 감지
            if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError" || errMsg.includes("Permission") || errMsg.includes("NotAllowed")) {
                isPermissionDenied = true;
            } else {
                toast.error("카메라를 시작할 수 없습니다: " + errMsg);
            }
        }
    }

    async function stopScanning() {
        if (!isInline && isHistoryPushed && !isPopStateTriggered) {
            isHistoryPushed = false;
            try {
                window.removeEventListener("popstate", handlePopState);
                window.history.back();
            } catch (e) {
                console.warn("스캐너 히스토리 복원 실패:", e);
            }
        } else {
            window.removeEventListener("popstate", handlePopState);
        }

        if (!html5Qrcode) {
            isScanning = false;
            dispatch("close");
            return;
        }
        try {
            if (html5Qrcode.isScanning) {
                await html5Qrcode.stop();
            }
            html5Qrcode = null;
            isScanning = false;
            dispatch("close");
        } catch (err) {
            console.error("스캐너 중지 오류:", err);
            html5Qrcode = null;
            isScanning = false;
            dispatch("close");
        }
    }

    function playBeep() {
        try {
            const ctx = new (window.AudioContext ||
                window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = 1000;
            gain.gain.setValueAtTime(0.2, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(
                0.01,
                ctx.currentTime + 0.12,
            );
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.12);
        } catch (e) {
            console.error("Beep sound error:", e);
        }
    }

    async function onScanSuccess(decodedText, decodedResult) {
        if (hasScanned) return;
        hasScanned = true;

        playBeep();

        // 💡 판정(성공/실패)은 부모 컴포넌트가 reportResult()를 호출해 알려준다.
        // 인라인 모드는 1.5초 뒤 자동으로 락 해제(연속 스캔), 풀스크린 모드는 실패 시에만 reportResult가 락을 풀어준다.
        dispatch("scan", decodedText);

        if (isInline) {
            setTimeout(() => {
                hasScanned = false;
            }, 1500);
        }
    }

    async function handleCameraChange() {
        if (html5Qrcode && html5Qrcode.isScanning) {
            await html5Qrcode.stop();
        }
        isScanning = false;
        await startScanning();
    }

    function getCameraLabel(label, index) {
        if (!label) {
            return `카메라 ${index + 1}`;
        }

        const lowerLabel = label.toLowerCase();

        // 1. 후면 카메라 관련 검색 및 번역
        if (
            lowerLabel.includes("back") ||
            lowerLabel.includes("rear") ||
            lowerLabel.includes("environment") ||
            lowerLabel.includes("후면") ||
            lowerLabel.includes("뒤")
        ) {
            let suffix = "";
            if (lowerLabel.includes("wide")) {
                suffix = " (광각)";
            } else if (lowerLabel.includes("telephoto")) {
                suffix = " (망원)";
            } else if (lowerLabel.includes("ultra")) {
                suffix = " (초광각)";
            }

            const numMatch = label.match(/\d+/);
            const numStr = numMatch ? ` ${numMatch[0]}` : "";

            return `후면 카메라${numStr}${suffix}`;
        }

        // 2. 전면 카메라 관련 검색 및 번역
        if (
            lowerLabel.includes("front") ||
            lowerLabel.includes("user") ||
            lowerLabel.includes("전면") ||
            lowerLabel.includes("앞")
        ) {
            const numMatch = label.match(/\d+/);
            const numStr = numMatch ? ` ${numMatch[0]}` : "";
            return `전면 카메라${numStr}`;
        }

        // 3. 영어 기본 단어 치환 필터
        return label
            .replace(/camera/gi, "카메라")
            .replace(/facing/gi, "방향")
            .replace(/back/gi, "후면")
            .replace(/front/gi, "전면")
            .replace(/external/gi, "외부");
    }

    onDestroy(async () => {
        if (!isInline && isHistoryPushed && !isPopStateTriggered) {
            isHistoryPushed = false;
            try {
                window.removeEventListener("popstate", handlePopState);
                window.history.back();
            } catch (e) {
                console.warn("스캐너 onDestroy 시 히스토리 복원 실패:", e);
            }
        } else {
            window.removeEventListener("popstate", handlePopState);
        }

        if (html5Qrcode) {
            try {
                if (html5Qrcode.isScanning) {
                    await html5Qrcode.stop();
                }
            } catch (e) {
                console.error("카메라 해제 중 오류:", e);
            }
            html5Qrcode = null;
        }
    });
</script>

<div class={isInline ? "inline-scanner" : "full-screen-scanner"} transition:fade={{ duration: 150 }}>
    <!-- 비디오 출력 영역 -->
    <div id="reader"></div>

    {#if isPermissionDenied}
        <!-- 💡 카메라 권한 거부 시 보여줄 모바일 앱 수동 허용 가이드 오버레이 -->
        <div class="permission-guide-overlay">
            <div class="permission-guide-card">
                <div class="guide-header-icon">📷</div>
                <h3>카메라 권한 차단됨</h3>
                <p class="guide-desc">바코드 스캔을 위해 카메라 사용 권한이 필요합니다.</p>
                <div class="guide-steps">
                    <div class="guide-step">
                        <span class="guide-step-badge">1</span>
                        <span class="guide-step-text">스마트폰 <strong>[설정]</strong> 앱 실행</span>
                    </div>
                    <div class="guide-step">
                        <span class="guide-step-badge">2</span>
                        <span class="guide-step-text"><strong>[애플리케이션]</strong> ➡️ <strong>[Retech]</strong> 앱 선택</span>
                    </div>
                    <div class="guide-step">
                        <span class="guide-step-badge">3</span>
                        <span class="guide-step-text"><strong>[권한]</strong> ➡️ <strong>[카메라]</strong>를 <strong>'허용'</strong>으로 설정</span>
                    </div>
                </div>
                {#if !isInline}
                    <button class="guide-close-btn" on:click={stopScanning}>닫기</button>
                {/if}
            </div>
        </div>
    {:else}
        <!-- 덮어씌울 조준 가이드 UI -->
        <div class="scanner-overlay">
            <div class="scanner-top-group">
                <header class="scanner-header">
                    <span class="pulse-dot"></span>
                    <span>실시간 바코드 스캔 중</span>
                </header>

                <div class="camera-control" on:click|stopPropagation>
                    <select
                        id="modal-camera-select"
                        bind:value={selectedCameraId}
                        on:change={handleCameraChange}
                    >
                        {#if availableCameras.length === 0}
                            <option value="">기본 카메라 (후면)</option>
                        {:else}
                            {#each availableCameras as cam, index}
                                <option value={cam.id}
                                    >{getCameraLabel(cam.label, index)}</option
                                >
                            {/each}
                        {/if}
                    </select>
                </div>
            </div>

            <div class="scanner-bottom-group">
                {#if !isInline}
                    <p class="scan-tip-msg">
                        바코드를 가이드선 안에 맞추어 주세요
                    </p>
                {/if}

                {#if !isInline}
                    <button class="btn-cancel-scan" on:click={stopScanning}>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                        >
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        스캔 취소
                    </button>
                {/if}
            </div>
        </div>
    {/if}

    <!-- 💡 스캔 결과 로그 바 (최근 1건 상시 표시, 클릭 시 최근 30건 오버레이) -->
    <button
        type="button"
        class="scan-log-bar"
        class:log-error={scanLogs[0]?.type === "error"}
        on:click={() => (isScanLogExpanded = !isScanLogExpanded)}
    >
        {#if scanLogs.length > 0}
            <span class="scan-log-time">{scanLogs[0].time}</span>
            <span class="scan-log-msg">{scanLogs[0].message}</span>
        {:else}
            <span class="scan-log-msg scan-log-empty">스캔 결과가 여기에 표시됩니다.</span>
        {/if}
        <span class="scan-log-toggle">{isScanLogExpanded ? "▼ 닫기" : "▲ 최근 내역"}</span>
    </button>

    {#if isScanLogExpanded}
        <div class="scan-log-overlay" transition:fade={{ duration: 150 }}>
            <div class="scan-log-overlay-header">
                최근 스캔 내역 ({scanLogs.length}건)
            </div>
            <div class="scan-log-list">
                {#each scanLogs as log}
                    <div class="scan-log-item" class:log-error={log.type === "error"}>
                        <span class="scan-log-time">{log.time}</span>
                        <span class="scan-log-msg">{log.message}</span>
                    </div>
                {:else}
                    <div class="scan-log-item scan-log-empty">스캔 내역이 없습니다.</div>
                {/each}
            </div>
        </div>
    {/if}
</div>

<style>
    .inline-scanner {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: 420px;
        background: #000;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: 8px;
        border: 2px solid #cbd5e1;
    }
    .full-screen-scanner {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: #000;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
    #reader {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        overflow: hidden; /* box-shadow가 부모 영역 밖으로 나가지 않도록 제한 */
    }
    #reader :global(video) {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
    }

    .scanner-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        padding: 40px 20px;
        box-sizing: border-box;
        z-index: 10;
        pointer-events: none;
    }

    /* 인라인 스캐너에서는 높이가 낮으므로 패딩을 좁혀 뷰포트 공간 최대로 확보 */
    .inline-scanner .scanner-overlay {
        padding: 15px 15px;
    }

    .scanner-top-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .scanner-bottom-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        width: 100%;
        /* 💡 하단 스캔 로그 바에 가리지 않도록 살짝 위로 띄움 */
        margin-bottom: 56px;
    }

    .inline-scanner .scanner-bottom-group {
        margin-bottom: 44px;
    }

    .scanner-header {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(15, 23, 42, 0.85);
        padding: 8px 18px;
        border-radius: 20px;
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }
    .pulse-dot {
        width: 8px;
        height: 8px;
        background: #ef4444;
        border-radius: 50%;
        animation: rec-pulse 0.8s infinite alternate;
    }
    @keyframes rec-pulse {
        0% {
            opacity: 0.4;
            transform: scale(0.9);
        }
        100% {
            opacity: 1;
            transform: scale(1.2);
        }
    }

    .camera-control {
        pointer-events: auto;
        z-index: 15;
    }
    .camera-control select {
        height: 38px;
        border: 1.5px solid rgba(255, 255, 255, 0.3);
        border-radius: 20px;
        padding: 0 16px;
        font-size: 0.82rem;
        font-weight: 700;
        background: rgba(15, 23, 42, 0.85);
        color: white;
        outline: none;
        cursor: pointer;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    }

    /* html5-qrcode가 동적으로 생성하는 실제 디코딩 스캔 영역 스타일링 */
    :global(#reader__scan_region) {
        border: 3px solid #10b981 !important; /* 실제 디코딩 스캔 영역에 초록 테두리 직접 주입 */
        border-radius: 6px;
        box-shadow: 
            0 0 15px rgba(16, 185, 129, 0.45),
            0 0 0 9999px rgba(15, 23, 42, 0.65) !important; /* 디코딩 영역 외부를 가려주는 오차가 절대 없는 불투명 마스크 효과 */
        position: relative;
        overflow: hidden;
    }
    /* 라이브러리가 그리는 내부 canvas 마스크(어긋남 원인)를 감추어 완벽한 일치 구현 */
    :global(#reader__scan_region canvas) {
        display: none !important;
    }


    .scan-tip-msg {
        font-size: 0.8rem;
        color: white;
        background: rgba(15, 23, 42, 0.85);
        padding: 7px 16px;
        border-radius: 20px;
        font-weight: 700;
        letter-spacing: -0.3px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
        text-align: center;
        max-width: 90%;
    }

    .btn-cancel-scan {
        pointer-events: auto;
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(255, 255, 255, 0.15);
        color: white;
        border: 1.5px solid rgba(255, 255, 255, 0.3);
        padding: 10px 24px;
        border-radius: 30px;
        font-size: 0.9rem;
        font-weight: 800;
        cursor: pointer;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        transition: all 0.2s;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    }
    .btn-cancel-scan:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: translateY(-2px);
    }

    /* 💡 카메라 권한 거부용 수동 안내 오버레이 및 카드 디자인 */
    .permission-guide-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(15, 23, 42, 0.93);
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        box-sizing: border-box;
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
    }
    .permission-guide-card {
        background: #ffffff;
        width: 100%;
        max-width: 380px;
        border-radius: 12px;
        padding: 24px;
        box-sizing: border-box;
        text-align: center;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
    }
    .guide-header-icon {
        width: 52px;
        height: 52px;
        background: #fee2e2;
        color: #ef4444;
        border-radius: 50%;
        font-size: 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: pulse-warn-anim 1.6s infinite alternate;
    }
    @keyframes pulse-warn-anim {
        0% { transform: scale(0.96); box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.2); }
        100% { transform: scale(1.04); box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
    }
    .permission-guide-card h3 {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 800;
        color: #1e293b;
    }
    .guide-desc {
        margin: 0;
        font-size: 0.8rem;
        color: #64748b;
        line-height: 1.45;
    }
    .guide-steps {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin: 10px 0;
        text-align: left;
    }
    .guide-step {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #f8fafc;
        padding: 8px 12px;
        border-radius: 6px;
        border: 1px solid #e2e8f0;
    }
    .guide-step-badge {
        width: 20px;
        height: 20px;
        background: #3b82f6;
        color: white;
        border-radius: 50%;
        font-size: 0.7rem;
        font-weight: 800;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .guide-step-text {
        font-size: 0.78rem;
        color: #334155;
        line-height: 1.35;
    }
    .guide-close-btn {
        width: 100%;
        height: 38px;
        background: #f1f5f9;
        color: #64748b;
        border: none;
        border-radius: 8px;
        font-size: 0.85rem;
        font-weight: 800;
        cursor: pointer;
        transition: background 0.2s;
    }
    .guide-close-btn:hover {
        background: #e2e8f0;
    }
    
    /* 인라인 스캐너용 컴팩트 모드 대응 */
    .inline-scanner .permission-guide-card {
        padding: 16px;
        gap: 8px;
    }
    .inline-scanner .guide-header-icon {
        width: 42px;
        height: 42px;
        font-size: 1.25rem;
    }
    .inline-scanner .permission-guide-card h3 {
        font-size: 1rem;
    }
    .inline-scanner .guide-desc {
        font-size: 0.75rem;
    }
    .inline-scanner .guide-step {
        padding: 6px 10px;
        gap: 8px;
    }
    .inline-scanner .guide-step-text {
        font-size: 0.72rem;
    }

    /* 💡 스캔 결과 로그 바 & 오버레이 */
    .scan-log-bar {
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 12px;
        z-index: 20;
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(15, 23, 42, 0.9);
        color: #e2e8f0;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        padding: 8px 12px;
        font-size: 0.78rem;
        font-weight: 700;
        cursor: pointer;
        text-align: left;
        font-family: inherit;
    }
    .scan-log-bar.log-error {
        background: rgba(127, 29, 29, 0.92);
        border-color: rgba(248, 113, 113, 0.4);
    }
    .scan-log-bar .scan-log-time {
        flex-shrink: 0;
        color: #94a3b8;
        font-size: 0.7rem;
        font-family: monospace;
    }
    .scan-log-bar .scan-log-msg {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .scan-log-bar .scan-log-empty {
        color: #94a3b8;
        font-weight: 500;
    }
    .scan-log-bar .scan-log-toggle {
        flex-shrink: 0;
        font-size: 0.68rem;
        color: #93c5fd;
    }

    .scan-log-overlay {
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 56px;
        z-index: 25;
        max-height: min(320px, 60%);
        display: flex;
        flex-direction: column;
        background: rgba(15, 23, 42, 0.95);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
    }
    .scan-log-overlay-header {
        flex-shrink: 0;
        padding: 8px 12px;
        font-size: 0.72rem;
        font-weight: 800;
        color: #93c5fd;
        border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    }
    .scan-log-list {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
    }
    .scan-log-item {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 7px 12px;
        font-size: 0.75rem;
        color: #e2e8f0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }
    .scan-log-item.log-error {
        color: #fca5a5;
    }
    .scan-log-item .scan-log-time {
        flex-shrink: 0;
        color: #94a3b8;
        font-size: 0.68rem;
        font-family: monospace;
    }
    .scan-log-item.scan-log-empty {
        color: #94a3b8;
        justify-content: center;
    }
</style>
